'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCmsQueryParamsState } from '../../hooks/useQueryParamsState';
import type { CmsProduct } from '../../types/cmsProduct';
import type { VerticalType } from '../../types/cmsTypes';
import { useSafeSearchParamsExtended } from '../../utils/useSafeSearchParams';
import { readCompareStorage, writeCompareStorage } from './compareStorage';
import {
  compareIdOf,
  COMPARE_URL_SCHEMA,
  CompareUrlState,
  normalizeCompareIds,
} from './compareUrl';
import type { CompareTracking } from './tracking';

export type CompareContextValue = {
  vertical: VerticalType;
  cap: number;
  /** Selected product uids in selection order. */
  selected: string[];
  isFull: boolean;
  isSelected: (id: string | undefined) => boolean;
  /** What the toggle did: nothing when the product has no id or the selection is at its cap. */
  toggle: (product: CmsProduct) => 'added' | 'removed' | undefined;
  /** Lets a card make its product known to the tray before it is ever selected. */
  register: (product: CmsProduct | undefined) => void;
  /** True once the selection has been restored from the URL or the record. */
  ready: boolean;
  remove: (id: string) => void;
  /** Drops every selected id not in `ids`, in one write; a no-op when nothing would change. */
  keepOnly: (ids: readonly string[]) => void;
  clear: () => void;
  /** Products the toggles have seen, so the tray can show a logo without another fetch. */
  productsById: ReadonlyMap<string, CmsProduct>;
  /** The comparison page for the current selection. */
  compareHref: (ids: readonly string[]) => string;
  /** Where the controls report what the reader did; absent, nothing is tracked. */
  tracking?: CompareTracking;
};

const CompareContext = createContext<CompareContextValue | null>(null);

/** `null` outside a provider: toggles render nothing on pages that do not compare. */
export const useCompare = (): CompareContextValue | null =>
  useContext(CompareContext);

export type CompareProviderProps = {
  vertical: VerticalType;
  /** 2–4; the config's `selectionCap`. */
  cap: number;
  compareHref: (ids: readonly string[]) => string;
  /**
   * Identity of the search the selection belongs to (the TI quote parameters). A different
   * scope drops a remembered selection, since the plans were priced for another trip. `null` while
   * the page cannot know it yet (the trip is read from the URL after hydration): nothing is
   * restored, recorded or dropped until it arrives, so the first real value is not taken for a
   * new search.
   */
  scope?: string | null;
  /** The page's analytics, for the vertical the tracking plan covers. */
  tracking?: CompareTracking;
  /**
   * Loads a selected product no card on this page has shown (picked on the comparison page, or
   * opened from a link), so the tray can name and picture it. Without it such a product shows its id.
   */
  loadProduct?: (id: string) => Promise<CmsProduct | undefined>;
  children: ReactNode;
};

/**
 * The comparison selection of one results page. The URL (`cms_compare`) is the source of
 * truth — a copied link opens the same selection — and sessionStorage remembers it for the
 * round trip results → comparison → results, where the results URL no longer carries it.
 */
export const CompareProvider = ({
  vertical,
  cap,
  compareHref,
  scope = '',
  tracking,
  loadProduct,
  children,
}: CompareProviderProps) => {
  // Every write replaces the current history entry: a selection change is not a navigation, and
  // Back must leave the page rather than step through each tick (the sort-and-filter contract sets
  // the same rule for every URL-held state on these pages).
  const [urlState, setUrlState] = useCmsQueryParamsState<CompareUrlState>(
    COMPARE_URL_SCHEMA,
    { replace: true },
  );
  const urlIds = useMemo(
    () => normalizeCompareIds(urlState.compare ?? []).slice(0, cap),
    [urlState.compare, cap],
  );

  const [selected, setSelected] = useState<string[]>(urlIds);
  // The selection as last written, ahead of the render that shows it: two changes in one tick
  // (two picks whose plans load together) each build on the other instead of on the same render.
  const latestSelected = useRef(selected);
  latestSelected.current = selected;
  // Every product a card has shown, so the tray can name a selected plan whose card has since been
  // filtered out.
  const productsRef = useRef(new Map<string, CmsProduct>());
  const [productsVersion, bumpProducts] = useState(0);
  const hydrated = useRef(false);
  // Exposed so consumers (the tray's announcements) can tell a restore from a reader's change.
  const [ready, setReady] = useState(false);

  // `useSafeSearchParams` publishes the query string from an effect, so the first render — and
  // every server render — sees no params at all. Hydrating before that would read an empty URL
  // and overwrite a shared link with the remembered selection.
  const searchParamsReady =
    useSafeSearchParamsExtended()?.searchParamsReady ?? false;

  /** Files `selected` under the current search. Nothing is written before the search is known. */
  const writeRecord = useCallback(
    (ids: string[]) => {
      if (scope === null) return;
      writeCompareStorage(vertical, { scope, selected: ids });
    },
    [vertical, scope],
  );

  // A scope change after a popstate that moved the query string is Back/Forward to an earlier
  // search; a modal's hash closing through Back fires popstate without moving it. `poppedTo` must
  // still match the URL, so a later new search's own URL is not taken for Back.
  const seenSearch = useRef<string | null>(null);
  useEffect(() => {
    seenSearch.current = window.location.search;
  });
  const poppedTo = useRef<string | null>(null);
  useEffect(() => {
    const onPop = () => {
      const search = window.location.search;
      if (search !== seenSearch.current) poppedTo.current = search;
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // A different search prices different plans, so a selection made for the previous one does not
  // carry over. The provider is not remounted when the trip changes, so this is where it goes.
  const knownScope = useRef(scope);
  useEffect(() => {
    if (scope === null) return;
    // The first known value is where the page starts, not a change of search.
    if (knownScope.current === null) {
      knownScope.current = scope;
      return;
    }
    if (knownScope.current === scope) {
      // Back to the search already on screen (through a quoting gap): nothing to restore.
      poppedTo.current = null;
      return;
    }
    knownScope.current = scope;
    const wentBack =
      poppedTo.current !== null && poppedTo.current === window.location.search;
    poppedTo.current = null;
    if (wentBack && urlIds.length > 0) {
      // Back or Forward to an earlier search: its URL still names that search's selection.
      setSelected(urlIds);
      writeRecord(urlIds);
      return;
    }
    setSelected([]);
    setUrlState({ compare: null });
    // A new search starts with nothing selected; the previous search's record goes with it.
    writeCompareStorage(vertical, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the search, not the setter
  }, [scope, vertical]);

  // The URL moved (Back/Forward, or a filter writing its own state with ours copied along): follow
  // it, and keep the remembered selection in step so a later return restores what is on screen.
  useEffect(() => {
    setSelected((current) => (sameIds(current, urlIds) ? current : urlIds));
    if (hydrated.current) writeRecord(urlIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the URL only
  }, [urlIds]);

  // Once the URL is actually known: an empty one inherits the remembered selection for the scope.
  useEffect(() => {
    if (hydrated.current || !searchParamsReady || scope === null) return;
    hydrated.current = true;
    setReady(true);
    const stored = readCompareStorage(vertical);

    // Back to this page from the comparison: the history entry still carries the selection it had
    // when the reader left, but plans removed on the comparison page — every one of them, possibly —
    // are gone from the record. The record is the newer truth for the same search.
    if (
      urlIds.length > 0 &&
      stored?.scope === scope &&
      navigationType() === 'back_forward'
    ) {
      const ids = stored.selected.slice(0, cap);
      setSelected(ids);
      setUrlState({ compare: ids.length ? ids : null });
      return;
    }

    // A link carries the selection the reader is opening; record it so a return to the results
    // page restores that one rather than whatever was picked before it.
    if (urlIds.length > 0) {
      writeRecord(urlIds);
      return;
    }
    // Only a record from another search is stale: it was made for other plans.
    const current = stored?.scope === scope ? stored : undefined;
    if (stored && !current) writeCompareStorage(vertical, null);
    if (!current || current.selected.length === 0) return;

    const ids = current.selected.slice(0, cap);
    setSelected(ids);
    setUrlState({ compare: ids });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, when the URL and the search are known
  }, [searchParamsReady, scope]);

  // A page restored from the back/forward cache keeps its old in-memory selection; the record may
  // have moved on (a plan removed on the comparison page). Re-read it for the same search.
  useEffect(() => {
    const onShow = (event: PageTransitionEvent) => {
      if (!event.persisted || knownScope.current === null) return;
      const stored = readCompareStorage(vertical);
      if (stored?.scope !== knownScope.current) return;
      const ids = stored.selected.slice(0, cap);
      setSelected(ids);
      setUrlState({ compare: ids.length ? ids : null });
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, [vertical, cap, setUrlState]);

  const commit = useCallback(
    (ids: string[]) => {
      latestSelected.current = ids;
      setSelected(ids);
      setUrlState({ compare: ids.length ? ids : null });
      writeRecord(ids);
    },
    [setUrlState, writeRecord],
  );

  /** A card makes its product known, so the tray can name it even once the card is gone. */
  const register = useCallback((product: CmsProduct | undefined) => {
    const id = compareIdOf(product);
    if (!id || !product || productsRef.current.has(id)) return;
    productsRef.current.set(id, product);
    bumpProducts((n) => n + 1);
  }, []);

  // A selected product the cards never showed is loaded once, after the cards have registered theirs
  // (their effects run before this one) and the selection is restored.
  const requested = useRef(new Set<string>());
  useEffect(() => {
    if (!ready || !loadProduct) return;
    for (const id of selected) {
      if (productsRef.current.has(id) || requested.current.has(id)) continue;
      requested.current.add(id);
      loadProduct(id)
        .then(register)
        // The tray still shows the id; nothing else depends on the product.
        .catch(() => undefined);
    }
  }, [selected, ready, loadProduct, register]);

  const value = useMemo<CompareContextValue>(() => {
    const isSelected = (id: string | undefined) =>
      !!id && selected.includes(id);
    return {
      vertical,
      cap,
      selected,
      isFull: selected.length >= cap,
      isSelected,
      toggle: (product) => {
        const id = compareIdOf(product);
        if (!id) return undefined;
        register(product);
        const current = latestSelected.current;
        if (current.includes(id)) {
          commit(current.filter((other) => other !== id));
          return 'removed';
        }
        if (current.length >= cap) return undefined;
        commit([...current, id]);
        return 'added';
      },
      register,
      ready,
      remove: (id) =>
        commit(latestSelected.current.filter((other) => other !== id)),
      keepOnly: (ids) => {
        const current = latestSelected.current;
        const kept = current.filter((id) => ids.includes(id));
        if (kept.length !== current.length) commit(kept);
      },
      clear: () => commit([]),
      productsById: productsRef.current,
      compareHref,
      tracking,
    };
    // `productsById` is a mutable map, so only this counter tells consumers a plan was registered.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above: the counter is the signal
  }, [
    vertical,
    cap,
    selected,
    commit,
    compareHref,
    register,
    ready,
    productsVersion,
    tracking,
  ]);

  return (
    <CompareContext.Provider value={value}>{children}</CompareContext.Provider>
  );
};

/** How this document was reached: `back_forward` when the reader came back to it through history. */
function navigationType(): string | undefined {
  try {
    const entry = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    return entry?.type;
  } catch {
    return undefined;
  }
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
